/**
 * Choose which connection to act through.
 *
 * Exists because one user can hold several connections to the same app — a work
 * GitHub and a personal one — and every action needs to know which. Emits a
 * connection_id and nothing else.
 */

import { useConnections } from './connection.context'
import { Logo, StatusBadge } from '@/shared/components'
import type { Connection } from '@/core/types/api.types'

interface Props {
  value: string | null
  onChange: (connectionId: string | null) => void
  /** Restrict to one connector, e.g. on a connector's own page. */
  connectorId?: string
  label?: string
  /** Hide connections that cannot currently run anything. */
  activeOnly?: boolean
}

export function ConnectionPicker({
  value,
  onChange,
  connectorId,
  label = 'Connection',
  activeOnly,
}: Props) {
  const { connections, isLoading } = useConnections()

  let options: Connection[] = connectorId
    ? connections.filter((c) => c.connector_id === connectorId)
    : connections
  if (activeOnly) options = options.filter((c) => c.status === 'active')

  const selected = options.find((c) => c.connection_id === value)

  return (
    <div className="field">
      <label className="field-label" htmlFor="connection-picker">
        {label}
      </label>

      <div className="row-tight">
        {selected && (
          <Logo
            src={selected.connector_logo}
            name={selected.connector_name ?? selected.connector_id}
            id={selected.connector_id}
            small
          />
        )}
        <select
          id="connection-picker"
          className="select"
          value={value ?? ''}
          disabled={isLoading || options.length === 0}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">
            {isLoading
              ? 'Loading connections…'
              : options.length === 0
                ? 'No connections yet — connect an app first'
                : 'Select a connection…'}
          </option>
          {options.map((conn) => (
            <option key={conn.connection_id} value={conn.connection_id}>
              {conn.label}
              {conn.status !== 'active' ? ` (${conn.status})` : ''}
            </option>
          ))}
        </select>
        {selected && <StatusBadge status={selected.status} />}
      </div>

      {selected && (
        <p className="field-help mono">
          {selected.connection_id}
        </p>
      )}
    </div>
  )
}
